import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Table from "../../core/pagination/datatable";
import AddUnit from "../../core/modals/inventory/addunit";
import EditUnit from "../../core/modals/inventory/editunit";
import { userActions } from "../../core/action/userAction";
import Swal from "sweetalert2";
import {
  Calendar,
  ChevronUp,
  Filter,
  PlusCircle,
  RotateCcw,
  Sliders,
  StopCircle,
  Zap,
} from "feather-icons-react/build/IconComponents";
import Select from "react-select";
import { DatePicker } from "antd";
import withReactContent from "sweetalert2-react-content";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { setToogleHeader } from "../../core/redux/action";
import { unitActions } from "../../core/action/unitaction";
import moment from "moment";

export const Units = () => {
  const dataSource = useSelector((state) => state.unit_list || []);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);

  // Permission checks
  const canCreate = userActions.hasPermission('create-units');
  const canEdit = userActions.hasPermission('edit-units');
  const canDelete = userActions.hasPermission('delete-units');
  //const canView = userActions.hasPermission('view-units');

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    unit: '',
    status: '',
    date: ''
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const [sortConfig, setSortConfig] = useState({ value: 'newest', label: 'Newest' });

  // Filter units client-side based on search term
  const getFilteredUnits = React.useMemo(() => {
    if (!searchTerm || !dataSource || dataSource.length === 0) {
      return dataSource;
    }

    const searchLower = searchTerm.toLowerCase().trim();

    return dataSource.filter(unit => {
      const name = (unit.unit_name || unit.name || '').toLowerCase();
      const description = (unit.description || '').toLowerCase();

      // Exact name match has highest priority
      if (name === searchLower) return true;

      // Name starts with search term
      if (name.startsWith(searchLower)) return true;

      // Description contains search term
      if (description.includes(searchLower)) return true;

      return false;
    });
  }, [searchTerm, dataSource]);

  const oldandlatestvalue = [
    { value: "date", label: "Sort by Date" },
    { value: "newest", label: "Newest" },
    { value: "oldest", label: "Oldest" },
  ];

  const unitOptions = [
    { value: "", label: "Choose Unit" },
    { value: "Piece", label: "Piece" },
    { value: "Kilogram", label: "Kilogram" },
    { value: "Gram", label: "Gram" },
  ];

  const statusOptions = [
    { value: "", label: "Choose Status" },
    { value: "Active", label: "Active" },
    { value: "InActive", label: "InActive" },
  ];

  const MySwal = withReactContent(Swal);

  // Fetch units
  const fetchUnits = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        status: filters.status,
        date: filters.date,
        sort_by: sortConfig.value === 'date' ? 'created_at' : 'created_at',
        sort_order: sortConfig.value === 'oldest' ? 'ASC' : 'DESC'
      };

      const result = await dispatch(unitActions.fetchUnits(params));
      
      if (result.success && result.data.pagination) {
        setPagination(prev => ({
          ...prev,
          total: result.data.pagination.total,
          totalPages: result.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error('Error fetching units:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch, pagination.page, pagination.limit, searchTerm, filters, sortConfig]);

  // Handle search
  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'click') {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchUnits();
    }
  };

  // Handle filter change
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  // Handle date change
  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      handleFilterChange('date', moment(date).format('YYYY-MM-DD'));
    } else {
      handleFilterChange('date', '');
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUnits();
    setIsFilterVisible(false);
  };

  // Clear filters
  const handleClearFilters = () => {
    setFilters({
      unit: '',
      status: '',
      date: ''
    });
    setSelectedDate(null);
    setSearchTerm('');
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchUnits();
  };

  // Handle sort change
  const handleSortChange = (option) => {
    setSortConfig(option);
    setPagination(prev => ({ ...prev, page: 1 }));
    // fetchUnits will be triggered by useEffect
  };

  // Handle delete unit
  const handleDeleteUnit = (id) => {
    if (!canDelete) {
      Swal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to delete units',
        icon: 'error'
      });
      return;
    }
    MySwal.fire({
      title: 'Are you sure?',
      text: 'You won\'t be able to revert this!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#00ff00',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonColor: '#ff0000',
      cancelButtonText: 'Cancel',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await dispatch(unitActions.deleteUnit(id));
        } catch (error) {
          // Error is handled in the action
        }
      }
    });
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchUnits();
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  // Table columns
  const columns = [
    {
      title: "Unit",
      dataIndex: "unit_name",
      sorter: (a, b) => a.unit_name.localeCompare(b.unit_name),
    },
    {
      title: "Short Name",
      dataIndex: "short_name",
      sorter: (a, b) => a.short_name.localeCompare(b.short_name),
    },
    {
      title: "NO of Products",
      dataIndex: "no_of_products",
      sorter: (a, b) => a.no_of_products - b.no_of_products,
    },
    {
      title: "Created On",
      dataIndex: "createdon",
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <span className={`badge ${text === 'Active' ? 'badge-linesuccess' : 'badge-linedanger'}`}>
          <Link to="#">{text}</Link>
        </span>
      ),
      sorter: (a, b) => a.display_status.localeCompare(b.display_status),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (_, record) => (
        <td className="action-table-data">
          <div className="edit-delete-action">
            {canEdit && (
            <Link 
              className="me-2 p-2"
              to="#"
              data-bs-toggle="modal"
              data-bs-target="#edit-units"
              onClick={() => handleEditUnit(record)}
            >
              <i data-feather="edit" className="feather-edit"></i>
            </Link>
            )}
            {canDelete && (
            <Link 
              className="confirm-text p-2" 
              to="#"
              onClick={() => handleDeleteUnit(record.id)}
            >
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
            )}
          </div>
        </td>
      ),
    },
  ];

  // Handle edit unit
  const [selectedUnit, setSelectedUnit] = useState(null);
  const handleEditUnit = (unit) => {
    if (!canEdit) {
      Swal.fire({
        title: 'Access Denied',
        text: 'You do not have permission to edit units',
        icon: 'error'
      });
      return;
    }
    setSelectedUnit(unit);
  };

  // Initial load
  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  // Update when sort changes
  useEffect(() => {
    fetchUnits();
  }, [sortConfig, pagination.page, pagination.limit]);

  const renderTooltip = (props) => (
    <Tooltip id="pdf-tooltip" {...props}>Pdf</Tooltip>
  );
  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>Excel</Tooltip>
  );
  const renderPrinterTooltip = (props) => (
    <Tooltip id="printer-tooltip" {...props}>Printer</Tooltip>
  );
  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>Refresh</Tooltip>
  );
  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>Collapse</Tooltip>
  );

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Units</h4>
                <h6>Manage your units</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderTooltip}>
                  <Link>
                    <ImageWithBasePath src="assets/img/icons/pdf.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
                  <Link data-bs-toggle="tooltip" data-bs-placement="top">
                    <ImageWithBasePath src="assets/img/icons/excel.svg" alt="img" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderPrinterTooltip}>
                  <Link data-bs-toggle="tooltip" data-bs-placement="top">
                    <i data-feather="printer" className="feather-printer" />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link 
                    data-bs-toggle="tooltip" 
                    data-bs-placement="top"
                    onClick={handleRefresh}
                  >
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => { dispatch(setToogleHeader(!data)) }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
            <div className="page-btn">
              {canCreate && (
              <a
                href="#"
                className="btn btn-added"
                data-bs-toggle="modal"
                data-bs-target="#add-units"
              >
                <PlusCircle className="me-2" />
                Add New Unit
              </a>
              )}
            </div>
          </div>
          
          {/* Unit List */}
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top">
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Search"
                      className="form-control form-control-sm formsearch"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
                    />
                    <Link 
                      to="#" 
                      className="btn btn-searchset"
                      onClick={handleSearch}
                    >
                      <i data-feather="search" className="feather-search" />
                    </Link>
                  </div>
                </div>
                <div className="search-path">
                  <Link 
                    className={`btn btn-filter ${isFilterVisible ? "setclose" : ""}`} 
                    id="filter_search"
                    onClick={() => setIsFilterVisible(!isFilterVisible)}
                  >
                    <Filter className="filter-icon" />
                    <span>
                      <ImageWithBasePath src="assets/img/icons/closes.svg" alt="img" />
                    </span>
                  </Link>
                </div>
                <div className="form-sort">
                  <Sliders className="info-img" />
                  <Select
                    className="select"
                    options={oldandlatestvalue}
                    value={sortConfig}
                    onChange={handleSortChange}
                    placeholder="Newest"
                  />
                </div>
              </div>
              
              {/* Filter Section */}
              <div
                className={`card${isFilterVisible ? " visible" : ""}`}
                id="filter_inputs"
                style={{ display: isFilterVisible ? "block" : "none" }}
              >
                <div className="card-body pb-0">
                  <div className="row">
                    <div className="col-lg-3 col-sm-6 col-12">
                      <div className="input-blocks">
                        <Zap className="info-img" />
                        <Select
                          className="select"
                          options={unitOptions}
                          placeholder="Choose Unit"
                          value={unitOptions.find(opt => opt.value === filters.unit)}
                          onChange={(selected) => handleFilterChange('unit', selected?.value || '')}
                        />
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6 col-12">
                      <div className="input-blocks">
                        <Calendar className="info-img" />
                        <div className="input-groupicon">
                          <DatePicker
                            selected={selectedDate}
                            onChange={handleDateChange}
                            className="filterdatepicker"
                            dateFormat="dd-MM-yyyy"
                            placeholder="Choose Date"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6 col-12">
                      <div className="input-blocks">
                        <StopCircle className="info-img" />
                        <Select
                          className="select"
                          options={statusOptions}
                          placeholder="Choose Status"
                          value={statusOptions.find(opt => opt.value === filters.status)}
                          onChange={(selected) => handleFilterChange('status', selected?.value || '')}
                        />
                      </div>
                    </div>
                    <div className="col-lg-3 col-sm-6 col-12 ms-auto">
                      <div className="input-blocks d-flex gap-2">
                        <button 
                          className="btn btn-filters"
                          onClick={handleApplyFilters}
                        >
                          <i data-feather="search" className="feather-search" /> Search
                        </button>
                        <button 
                          className="btn btn-secondary"
                          onClick={handleClearFilters}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Unit Table */}
              <div className="table-responsive">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading units...</p>
                  </div>
                ) : getFilteredUnits.length === 0 ? (
                  <div className="text-center py-5">
                    <i data-feather="package" className="feather-package" style={{ fontSize: '48px', opacity: 0.5 }} />
                    <h5 className="mt-3">No units found</h5>
                    <p className="text-muted">
                      {searchTerm || Object.values(filters).some(v => v)
                        ? "Try adjusting your search or filters"
                        : "Add your first unit to get started"}
                    </p>
                  </div>
                ) : (
                  <>
                    <Table columns={columns} dataSource={getFilteredUnits} />
                    
                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                      <div className="pagination-wrapper mt-3">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="pagination-info">
                            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                          </div>
                          <div className="pagination-controls">
                            <select 
                              className="form-select form-select-sm me-2"
                              value={pagination.limit}
                              onChange={(e) => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value), page: 1 }))}
                              style={{ width: 'auto' }}
                            >
                              <option value="10">10 per page</option>
                              <option value="20">20 per page</option>
                              <option value="50">50 per page</option>
                              <option value="100">100 per page</option>
                            </select>
                            
                            <nav>
                              <ul className="pagination pagination-sm mb-0">
                                <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => handlePageChange(pagination.page - 1)}
                                    disabled={pagination.page === 1}
                                  >
                                    Previous
                                  </button>
                                </li>
                                
                                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                  let pageNum;
                                  if (pagination.totalPages <= 5) {
                                    pageNum = i + 1;
                                  } else if (pagination.page <= 3) {
                                    pageNum = i + 1;
                                  } else if (pagination.page >= pagination.totalPages - 2) {
                                    pageNum = pagination.totalPages - 4 + i;
                                  } else {
                                    pageNum = pagination.page - 2 + i;
                                  }
                                  
                                  return (
                                    <li 
                                      key={pageNum} 
                                      className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}
                                    >
                                      <button 
                                        className="page-link" 
                                        onClick={() => handlePageChange(pageNum)}
                                      >
                                        {pageNum}
                                      </button>
                                    </li>
                                  );
                                })}
                                
                                <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                  <button 
                                    className="page-link" 
                                    onClick={() => handlePageChange(pagination.page + 1)}
                                    disabled={pagination.page === pagination.totalPages}
                                  >
                                    Next
                                  </button>
                                </li>
                              </ul>
                            </nav>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          
          <AddUnit onSuccess={fetchUnits} />
          <EditUnit unit={selectedUnit} onSuccess={fetchUnits} />
        </div>
      </div>
    </>
  );
};